import { motion } from "framer-motion";
import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Mail, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { getRecaptchaToken } from "@/lib/recaptcha";
import { useActiveConsent } from "@/hooks/use-active-consent";

type FieldError = { field: "email" | "consent" | "form"; message: string } | null;

export function IntelligenceBriefsCapture() {
  const { version: CONSENT_VERSION, consent_text: CONSENT_TEXT } = useActiveConsent();
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [fieldError, setFieldError] = useState<FieldError>(null);

  const consentDisplayedAt = useRef<string>(new Date().toISOString());
  const emailRef = useRef<HTMLInputElement>(null);
  const consentRef = useRef<HTMLButtonElement>(null);

  const focusField = (field: NonNullable<FieldError>["field"]) => {
    requestAnimationFrame(() => {
      if (field === "email") emailRef.current?.focus();
      else if (field === "consent") consentRef.current?.focus();
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFieldError(null);
    if (!email) return;

    if (!consent) {
      setFieldError({ field: "consent", message: "Please confirm you agree to the Privacy Policy." });
      focusField("consent");
      return;
    }

    setLoading(true);
    try {
      const recaptchaToken = await getRecaptchaToken("newsletter_signup");
      const { data, error } = await supabase.functions.invoke("subscribe-newsletter", {
        body: {
          email,
          source: "homepage-briefs-capture",
          recaptchaToken,
          gdprConsent: consent,
          consentText: CONSENT_TEXT,
          consentVersion: CONSENT_VERSION,
          consentDisplayedAt: consentDisplayedAt.current,
        },
      });

      const payload = data as { error?: string; code?: string } | null;
      if (error || payload?.error) {
        const code = payload?.code ?? "server_error";
        const message = payload?.error ?? "Please try again.";
        const field: "email" | "consent" | "form" =
          code === "invalid_email" ? "email" : code === "consent_required" ? "consent" : "form";
        setFieldError({ field, message });
        focusField(field);
        return;
      }

      setSubmitted(true);
      toast({ title: "Confirmed", description: "Check your inbox for your first brief." });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Please try again.";
      setFieldError({ field: "form", message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-16 lg:py-20 border-t border-border/30">
      <div className="max-w-2xl mx-auto px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center justify-center gap-2 mb-4">
            <Mail className="h-4 w-4 text-accent" />
            <h2 className="text-[10px] uppercase tracking-[0.3em] text-accent font-semibold">
              Get the corrections first
            </h2>
          </div>
          <p className="text-[15px] text-muted-foreground leading-[1.75] mb-8 max-w-md mx-auto">
            Occasional notes when a figure is published, sourced, or corrected. No spam.
          </p>

          {submitted ? (
            <p className="text-[14px] text-accent font-medium">Confirmed. You're on the list.</p>
          ) : (
            <form onSubmit={handleSubmit} className="max-w-md mx-auto space-y-3" noValidate>
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1 text-left">
                  <Input
                    ref={emailRef}
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    aria-invalid={fieldError?.field === "email"}
                    aria-describedby={fieldError?.field === "email" ? "briefs-email-error" : undefined}
                    className="h-11 text-[14px]"
                  />
                  {fieldError?.field === "email" && (
                    <p id="briefs-email-error" className="mt-1 text-[12px] text-destructive inline-flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" /> {fieldError.message}
                    </p>
                  )}
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-foreground text-background hover:bg-foreground/90 h-11 px-6 text-[13px] font-medium shrink-0"
                >
                  {loading ? "..." : "Subscribe"}
                </Button>
              </div>

              <label className="flex items-start gap-2 text-left text-[12px] text-muted-foreground cursor-pointer">
                <Checkbox
                  ref={consentRef}
                  checked={consent}
                  onCheckedChange={(v) => { setConsent(v === true); if (v) setFieldError(null); }}
                  aria-invalid={fieldError?.field === "consent"}
                  aria-describedby={fieldError?.field === "consent" ? "briefs-consent-error" : undefined}
                  className="mt-0.5"
                />
                <span>
                  {CONSENT_TEXT}{" "}
                  <Link to="/privacy" className="underline">Privacy Policy</Link>.
                  <span className="block text-[10px] opacity-60 mt-0.5">Consent {CONSENT_VERSION}</span>
                </span>
              </label>
              {fieldError?.field === "consent" && (
                <p id="briefs-consent-error" className="text-[12px] text-destructive inline-flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" /> {fieldError.message}
                </p>
              )}

              {fieldError?.field === "form" && (
                <p role="alert" className="text-[12px] text-destructive inline-flex items-center gap-1 justify-center">
                  <AlertCircle className="h-3 w-3" /> {fieldError.message}
                </p>
              )}
            </form>
          )}

          <p className="text-[11px] text-muted-foreground/40 mt-4 font-mono">
            Selective distribution · Decision-makers only
          </p>
        </motion.div>
      </div>
    </section>
  );
}
